import cn from 'classnames'
import React, { ComponentProps, CSSProperties, ReactElement } from 'react'

import style, { VALUE_CUSTOM_PROPERTY } from './ProgressBar.style'

type ProgressBarProps = {
  className?: string
  variant?: 'percentage' | 'indeterminate'
  /**
   * Completion from 0 to 100. `percentage` variant only — ignored by
   * `indeterminate`, which has no notion of progress.
   *
   * Values outside the range are clamped rather than rejected, and a non-finite
   * value (the `NaN` a `balance / limit` division produces when the limit is 0)
   * renders as 0 instead of reaching the DOM as an invalid width. That matters
   * because `aria-valuenow` must stay inside min/max for the role to be valid.
   */
  value?: number
} & ComponentProps<'div'>

/**
 * Progress indicator.
 *
 * `indeterminate` (the default) animates a fixed-width bar back and forth for
 * work of unknown duration. `percentage` fills proportionally to `value`.
 *
 * ACCESSIBILITY: the `percentage` variant exposes `role="progressbar"`, so it
 * needs an accessible name — pass `aria-label`, or `aria-labelledby` pointing at
 * a visible label. The `indeterminate` variant deliberately carries no role: it
 * conveys nothing a screen reader can act on, and adding one would change
 * behavior for the existing consumer.
 */
function ProgressBar(props: ProgressBarProps): ReactElement {
  const {
    className = '',
    variant = 'indeterminate',
    value,
    style: inlineStyle,
    ...rest
  } = props

  const isPercentage = variant === 'percentage'
  const clampedValue =
    value !== undefined && Number.isFinite(value)
      ? Math.min(100, Math.max(0, value))
      : 0

  // A custom property rather than a width in the inline style, so the fill stays
  // described in the style file with the rest of the component's geometry. This
  // is the sanctioned inline-style exception: a dynamic value, not styling.
  const combinedStyle = isPercentage
    ? ({
        ...inlineStyle,
        [VALUE_CUSTOM_PROPERTY]: `${clampedValue}%`,
      } as CSSProperties)
    : inlineStyle

  return (
    <div
      className={cn('ProgressBar', className, variant)}
      css={style}
      style={combinedStyle}
      {...(isPercentage
        ? {
            role: 'progressbar',
            'aria-valuenow': clampedValue,
            'aria-valuemin': 0,
            'aria-valuemax': 100,
          }
        : {})}
      {...rest}
    >
      <div className="ProgressBar-inner"></div>
    </div>
  )
}

export default ProgressBar
